import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { TextArea as TextAreaWrapper } from "shared/ui/ae-user-input";
import { useEmailTemplatesContext } from "@/modules/email-templates-sidebar/context";
import { glencocoClientAPI } from "@/api/glencoco";
import toast from "react-hot-toast";
import throttle from "lodash/throttle";
import { clsxMerge } from "shared/lib/helpers";
import { getFormattedLastUpdatedAt } from "shared/lib/helpers/date";
import { LoadingComponent } from "@/components/modules/loading";
import { dispatchCustomEvent } from "@/helpers/events";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import { ValueOfObjectFields } from "shared/lib/interfaces/utils";

import { swallow } from "@/api/legacy-swallow";

const TextArea = TextAreaWrapper();

export const PLACEHOLDER_TYPES = {
  LONG: "long",
  SHORT: "short",
} as const;

const PLACEHOLDER_LONG = `Example: Send {first name} an email reviewing pricing and avg cost savings for customers. Include a one-page and be super positive. Also ask for a good time to follow-up.\n---\nEnter your custom prompt here`;
const PLACEHOLDER_SHORT = "Enter your custom prompt here";

export const PromptTextarea: FC<
  PropsWithClassNameI & {
    classNameTextarea?: string;
    isHeightLimit?: boolean;
    placeholderType?: ValueOfObjectFields<typeof PLACEHOLDER_TYPES>;
    onChange?: (prompt: string) => void;
    disabled?: boolean;
  }
> = ({
  className,
  classNameTextarea,
  isHeightLimit = true,
  placeholderType = PLACEHOLDER_TYPES.SHORT,
  onChange,
  disabled,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const { activeTemplate, refetchTemplates, setPromptDraft, clearPromptDraft } =
    useEmailTemplatesContext();

  const [prompt, setPrompt] = useState(activeTemplate?.prompt || "");
  const [isLoading, setIsLoading] = useState(false);

  const updatedAt = useMemo(() => {
    return getFormattedLastUpdatedAt(activeTemplate?.updated_at as string);
  }, [activeTemplate?.updated_at]);

  /**
   * Compose reads the prompt from here rather than waiting for the throttled
   * save to land: clicking Compose within 3 seconds of the last keystroke used
   * to generate from the previous text.
   *
   * Only the rep's own edits are published, and hydrating a template clears
   * whatever was there. Seeding the draft with the template's saved prompt
   * looked harmless - the value is the same column the server reads - but it
   * made the draft non-empty on every compose, so `info.user_prompt` from
   * `fetch_info_for_email_generation` became unreachable and the client
   * dictated the prompt even for read-only templates the rep cannot edit.
   * Publishing on edit only keeps the server authoritative until someone
   * actually types.
   *
   * Published at the write site rather than from an effect on [prompt,
   * templateId], because an effect sees the new template id in the same pass
   * that the reset below has only *queued* the new prompt - so for one render
   * it would file the outgoing template's instructions under the incoming
   * template's id.
   */
  useEffect(() => {
    if (activeTemplate?.id) {
      setPrompt(activeTemplate?.prompt || "");
      clearPromptDraft(activeTemplate.id);
    }
  }, [activeTemplate?.id]);

  useEffect(() => {
    onChange?.(prompt);
  }, [prompt]);

  const updatePrompt = (prompt: string) => {
    if (!activeTemplate?.id) return;

    (async () => {
      setIsLoading(true);
      const API = glencocoClientAPI();

      const UpdateAccountNoteReponse = await swallow(
        API.email.updateEmailTemplate(activeTemplate?.id as string, {
          prompt,
        })
      ).catch((e) => e);

      if (UpdateAccountNoteReponse?.status !== 200) {
        toast.error("Failed to update prompt");
        setIsLoading(false);
        return;
      }

      await refetchTemplates();
      setIsLoading(false);
    })();
  };

  const throttledOnChange = useCallback(throttle(updatePrompt, 3000), [
    activeTemplate?.id,
  ]);

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const prompt = e.target.value;

    setPrompt(prompt);

    if (activeTemplate?.id) {
      setPromptDraft(activeTemplate.id, prompt);
    }

    throttledOnChange(prompt);
  };

  const handleOnBlur = () => {
    /**
     * Leaving the field is the rep saying they are done typing, so the pending
     * throttled write goes out now instead of up to 3 seconds later. Compose
     * no longer depends on this - it reads the on-screen text - but the next
     * session loads whatever the server holds.
     */
    throttledOnChange.flush();

    dispatchCustomEvent({
      eventType: CUSTOM_EVENTS.ACCOUNT.EMAIL_TEMPLATES.PROMT_BLUR,
    });

    if (textAreaRef.current) {
      textAreaRef.current.scrollTop = 0;
    }
  };

  return (
    <section
      className={clsxMerge("flex animate-fadein flex-col gap-3", className)}
    >
      <div className="flex w-full items-center justify-between">
        <h6 className="typography-body-4-medium">Prompt logic</h6>

        {activeTemplate?.id && (
          <div className="min-h-[24px]">
            {isLoading && (
              <LoadingComponent
                delay={300}
                className="absolute right-4 top-[6px] animate-fadein"
              />
            )}{" "}
            {!isLoading && !disabled && updatedAt && (
              <span className="animate-fadein text-black/60 typography-body-4">
                Auto saved at {updatedAt}
              </span>
            )}
          </div>
        )}
      </div>

      <TextArea
        inputProps={{
          value: prompt,
          onChange: handleOnChange,
          onBlur: handleOnBlur,
          ref: textAreaRef,
        }}
        className={clsxMerge(
          "mb-0 w-full rounded-lg border border-black/20 p-0"
        )}
        inputClassName={clsxMerge(
          "mb-0 bg-white",
          "focus:outline-none focus:ring-0",
          "transition-all duration-300",

          /**
           * #ccc on white is roughly 1.6:1. The hint that tells a rep what to
           * write here was the palest thing on the panel and read as disabled
           * text. #666 is what the long variant already used, so this is the
           * darker of the two tones the component itself shipped, applied to
           * both.
           */
          "placeholder:text-[#666]",

          /**
           * Draggable, like the transcript box directly above it. The prompt is
           * the one field a rep iterates on, and it was the only one they could
           * not size.
           */
          "resize-y",

          "[field-sizing:content]",
          "bg-transparent",
          "px-4 pt-2",
          "rounded-none border-none outline-none",
          "typography-body-4",

          /**
           * 50px is two lines. A prompt of any substance was therefore edited
           * through a slot showing a fraction of itself, and the rest was
           * unreachable without focusing the field first - `overflow-hidden`
           * clipped it and nothing scrolled. Four lines at rest, room to grow
           * to ten, and the whole thing scrollable either way.
           */
          isHeightLimit && "max-h-[240px] min-h-[96px]",
          isHeightLimit && "w-full focus:max-h-[40vh] focus:min-h-[96px]",

          "overflow-auto",

          disabled && "pointer-events-none cursor-not-allowed",

          classNameTextarea
        )}
        placeholder={
          placeholderType === PLACEHOLDER_TYPES.LONG
            ? PLACEHOLDER_LONG
            : PLACEHOLDER_SHORT
        }
      />
    </section>
  );
};
